/**
 * Uniform `.cn` date display (cn-uniform-date-format).
 * Not related to `chinaFormattedDate` in RoomTypeSection/utils.
 *
 * - `format*`: parsers/formatters for tests or advanced use.
 * - `renderChina*`: use in JSX when config.IS_CHINA_APP (else keep existing label).
 */

export type DisplayDateStyle = "numeric" | "named";

type CalendarParts = { y: number; m: number; d: number };

const ENGLISH_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTH_SHORT_TO_NUM: Record<string, number> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

const WEEKDAY_ZH = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];

function toYmd({ y, m, d }: CalendarParts): string {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function parseYmd(value: string): CalendarParts | null {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
}

function parseYmdMonth(value: string): { y: number; m: number } | null {
  const match = value.match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;
  return { y: Number(match[1]), m: Number(match[2]) };
}

function parseDdMmYyyy(value: string): CalendarParts | null {
  const match = value.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (!match) return null;
  return { y: Number(match[3]), m: Number(match[2]), d: Number(match[1]) };
}

function parseMonYyyy(value: string): { y: number; m: number } | null {
  const match = value
    .trim()
    .toLowerCase()
    .match(/^([a-z]{3})-(\d{4})$/);
  if (!match) return null;
  const m = MONTH_SHORT_TO_NUM[match[1]];
  if (!m) return null;
  return { y: Number(match[2]), m };
}

function parseEnglishLongMonth(value: string): CalendarParts | null {
  const match = value.match(/^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})$/);
  if (!match) return null;
  const monthIndex = ENGLISH_MONTHS.findIndex((m) => m.toLowerCase() === match[1].toLowerCase());
  if (monthIndex < 0) return null;
  return { y: Number(match[3]), m: monthIndex + 1, d: Number(match[2]) };
}

/** e.g. review booking_details.tenancy: `Sep 1, 2025` */
function parseEnglishAbbrevMonthDayYear(value: string): CalendarParts | null {
  const match = value.match(/^([A-Za-z]{3})\s+(\d{1,2}),?\s+(\d{4})$/);
  if (!match) return null;
  const m = MONTH_SHORT_TO_NUM[match[1].toLowerCase()];
  if (!m) return null;
  return { y: Number(match[3]), m, d: Number(match[2]) };
}

function parseEnglishShortMonth(value: string): CalendarParts | null {
  const match = value.match(/^(\d{1,2})\s+([A-Za-z]{3}),?\s+(\d{4})$/);
  if (!match) return null;
  const m = MONTH_SHORT_TO_NUM[match[2].toLowerCase()];
  if (!m) return null;
  return { y: Number(match[3]), m, d: Number(match[1]) };
}

function parseEnglishOrdinalShortMonth(value: string): CalendarParts | null {
  const match = value.match(/^(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]{3}),?\s+(\d{4})$/i);
  if (!match) return null;
  const m = MONTH_SHORT_TO_NUM[match[2].toLowerCase()];
  if (!m) return null;
  return { y: Number(match[3]), m, d: Number(match[1]) };
}

function parseIsoTimestamp(value: string): CalendarParts | null {
  if (!value.includes("T")) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const ymd = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
  return parseYmd(ymd);
}

export function parseCalendarInput(value: unknown): CalendarParts | null {
  if (value === undefined || value === null || value === "") return null;
  const str = String(value).trim();
  if (!str) return null;

  if (str.includes("T")) {
    const fromIso = parseIsoTimestamp(str);
    if (fromIso) return fromIso;
  }

  return (
    parseYmd(str) ||
    parseDdMmYyyy(str) ||
    parseEnglishOrdinalShortMonth(str) ||
    parseEnglishShortMonth(str) ||
    parseEnglishAbbrevMonthDayYear(str) ||
    parseEnglishLongMonth(str) ||
    parseIsoTimestamp(str) ||
    null
  );
}

/** Local calendar parts from a parsed `Date` (bookings move-in/out display). */
export function localDateToYmd(jsDate: Date): string {
  return `${jsDate.getFullYear()}-${String(jsDate.getMonth() + 1).padStart(2, "0")}-${String(
    jsDate.getDate(),
  ).padStart(2, "0")}`;
}

const COMPOSITE_RANGE_SPLIT = /\s+(?:-|–|—)\s+|\s+to\s+/i;

function splitCompositeRangeLabel(value: string): [string, string] | null {
  const parts = value
    .split(COMPOSITE_RANGE_SPLIT)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 2) return null;
  return [parts[0], parts[1]];
}

function parseMonthInput(value: unknown): { y: number; m: number } | null {
  if (value === undefined || value === null || value === "") return null;
  const str = String(value).trim();
  if (!str) return null;
  return parseMonYyyy(str) || parseYmdMonth(str) || null;
}

export function formatDisplayDate(value: unknown, style: DisplayDateStyle): string {
  const parts = parseCalendarInput(value);
  if (!parts) return "";
  if (style === "numeric") return toYmd(parts);
  return `${parts.y}年${parts.m}月${parts.d}日`;
}

export function ymdToDdMmYyyy(ymd: string): string {
  const parts = parseYmd(ymd.trim());
  if (!parts) return "";
  return `${String(parts.d).padStart(2, "0")}-${String(parts.m).padStart(2, "0")}-${parts.y}`;
}

export function formatDisplayMonth(value: unknown, style: DisplayDateStyle): string {
  const monthParts = parseMonthInput(value);
  if (monthParts) {
    if (style === "numeric") {
      return `${monthParts.y}-${String(monthParts.m).padStart(2, "0")}`;
    }
    return `${monthParts.y}年${monthParts.m}月`;
  }
  const dayParts = parseCalendarInput(value);
  if (!dayParts) return "";
  if (style === "numeric") {
    return `${dayParts.y}-${String(dayParts.m).padStart(2, "0")}`;
  }
  return `${dayParts.y}年${dayParts.m}月`;
}

export function formatDisplayRange(start: unknown, end: unknown, style: DisplayDateStyle): string {
  const left = formatDisplayDate(start, style);
  const right = formatDisplayDate(end, style);
  if (left && right) return `${left} 至 ${right}`;
  return "";
}

export function formatDisplayDateWithWeekday(value: unknown): string {
  const named = formatDisplayDate(value, "named");
  if (!named) return "";
  const parts = parseCalendarInput(value);
  if (!parts) return named;
  const weekday = WEEKDAY_ZH[new Date(parts.y, parts.m - 1, parts.d).getDay()];
  return `${named} ${weekday}`;
}

export function formatChinaChartMonthLabel(englishLabel: string, year: number | string): string {
  const m = MONTH_SHORT_TO_NUM[String(englishLabel).trim().toLowerCase().slice(0, 3)];
  const y = Number(year);
  if (!m || !Number.isFinite(y)) return "";
  return `${y}年${m}月`;
}

function withFallback(formatted: string, value: unknown, fallback: unknown): string {
  if (formatted) return formatted;
  if (fallback !== undefined && fallback !== null && String(fallback).trim() !== "") {
    return String(fallback);
  }
  if (value !== undefined && value !== null && String(value).trim() !== "") {
    return String(value);
  }
  return "";
}

export function renderChinaNamedDate(value: unknown, fallback?: unknown): string {
  return withFallback(formatDisplayDate(value, "named"), value, fallback);
}

export function renderChinaNamedMonth(value: unknown, fallback?: unknown): string {
  return withFallback(formatDisplayMonth(value, "named"), value, fallback);
}

export function renderChinaNamedRange(start: unknown, end: unknown, fallback?: unknown): string {
  return withFallback(formatDisplayRange(start, end, "named"), start, fallback);
}

/** Pre-built range strings (e.g. review `booking_details.tenancy`: `Sep 1, 2025 - Aug 31, 2026`). */
export function renderChinaCompositeDateRange(value: unknown, fallback?: unknown): string {
  const str = value === undefined || value === null ? "" : String(value).trim();
  if (!str) return withFallback("", value, fallback);

  const rangeParts = splitCompositeRangeLabel(str);
  if (rangeParts) {
    return withFallback(formatDisplayRange(rangeParts[0], rangeParts[1], "named"), value, fallback);
  }

  const single = formatDisplayDate(str, "named") || formatDisplayMonth(str, "named");
  return withFallback(single, value, fallback);
}

export function renderChinaNamedDateWithWeekday(value: unknown, fallback?: unknown): string {
  return withFallback(formatDisplayDateWithWeekday(value), value, fallback);
}

export function renderChinaNumericDate(value: unknown, fallback?: unknown): string {
  return withFallback(formatDisplayDate(value, "numeric"), value, fallback);
}
